// src/components/SimpleComponents/SimpleCountry.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import useZustand from 'hooks/useZustand';
import { Select } from 'antd';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import { useSimpleFormContext } from './SimpleFormProvider';
import countriesProfiles from 'assets/data/countriesProfiles.json';
import { getStaticLocaleFromApp } from 'utils/localeUtils';
import translateX from 'utils/translateX';
import getValueFromFlattenObj from 'utils/getValueFromFlattenObj';
import app from 'configs/appResolver';
import { getFlag } from 'utils/phone';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const FLAG_IMG_STYLE = { width: 24, height: 16, objectFit: 'cover', borderRadius: 2 };
const DROPDOWN_ITEM_STYLE = { display: 'inline-flex', alignItems: 'center', gap: 8 };
const SELECTED_ITEM_STYLE = { display: 'inline-flex', alignItems: 'center', gap: 6 };
const NAME_NOWRAP_STYLE = { whiteSpace: 'nowrap' };

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleCountry = ({
  name = 'countryCode',
  subdocumentField,
  styleItem = {},
  disabled = false,
  countryNamesLang, // if not provided, fallback to app static locale
  availableCountries, // optional whitelist of country codes
  onCountryChange, // optional callback receiving lowercased country code
  allowClear = true,
  placeholder,
  size = 'middle',
  popupMatchSelectWidth = true,
  optionLabelProp = 'labelShort', // compact label on trigger

  // defaults / legacy
  defaultCountry, // preferred default - if not provided, will use app.LOCALE
  initialValue, // optional "wished" initial value (we'll set via form.setFieldsValue)
  countryCode: countryCodeProp, // parent countryCode (precedence: dataFromDb -> countryCodeProp -> app locale)
  associatedValueField, // optional field to check if empty (e.g., 'address1' or 'zip') - only updates countryCode if this field is empty
  subscriptionCountryCode, // country from subscription (priority 1)
  userCountryCode, // country from logged-in user (priority 2)

  // select-only extras (will not leak to DOM nodes)
  selectProps = {},

  // anything else goes to SimpleFormItem but beware it may forward to DOM;
  // we keep it narrow to avoid leaking unknown props like initialValue.
  ..._unsafeItemProps
}) => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const { locked, form, disabledFields, data, initialValues } = useSimpleFormContext();
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const storedLanguage = useZustand('theme', 'language');
  const selectRef = useRef(null);
  const wrapperRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);

  // 2. LOCAL VARIABLES I
  const style = { ...(_unsafeItemProps?.style || {}) };

  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  const lang = useMemo(() => {
    const appLocale = (countryNamesLang || getStaticLocaleFromApp() || 'pt').toUpperCase();
    return normalizeLang(appLocale);
  }, [countryNamesLang]);

  const appDefaultCountry = useMemo(() => {
    const locale = app.LOCALE || 'en-US';
    const cc = locale.includes('-') ? locale.split('-').pop() : locale;
    return (cc || 'US').toUpperCase();
  }, []);

  const extractCountryCode = useCallback(value => {
    if (!value) return null;
    const v = value.toString().trim();
    let code;
    if (v.includes('-')) {
      code = v.split('-').pop().toUpperCase();
    } else {
      code = v.toUpperCase();
    }
    return code.length === 2 ? code : null;
  }, []);

  const initialValuesCountryCode = useMemo(() => {
    if (!initialValues) return null;
    if (Array.isArray(fullFieldName)) {
      return fullFieldName.reduce((obj, key) => obj?.[key], initialValues);
    }
    return initialValues?.[fullFieldName] || initialValues?.countryCode;
  }, [initialValues, fullFieldName]);

  const effectiveCountryCode = useMemo(() => {
    const dataFromDb = getValueFromFlattenObj(data, flattenFieldName);
    const resolved =
      extractCountryCode(dataFromDb) ||
      extractCountryCode(initialValuesCountryCode) ||
      extractCountryCode(subscriptionCountryCode) ||
      extractCountryCode(userCountryCode) ||
      extractCountryCode(countryCodeProp) ||
      extractCountryCode(initialValue) ||
      extractCountryCode(defaultCountry) ||
      appDefaultCountry ||
      'US';
    return resolved || 'US';
  }, [
    data,
    flattenFieldName,
    initialValuesCountryCode,
    subscriptionCountryCode,
    userCountryCode,
    countryCodeProp,
    initialValue,
    defaultCountry,
    appDefaultCountry,
    extractCountryCode,
  ]);

  const allowedCodes = useMemo(() => {
    const allCodes = (countriesProfiles || []).map(c => (c?.countryCode || '').toUpperCase());
    if (!Array.isArray(availableCountries) || availableCountries.length === 0) return allCodes;
    return availableCountries.map(c => (c || '').toUpperCase()).filter(Boolean);
  }, [availableCountries]);

  const options = useMemo(() => {
    const prepared = (countriesProfiles || [])
      .filter(c => allowedCodes.includes((c?.countryCode || '').toUpperCase()))
      .map(c => {
        const code = (c?.countryCode || '').toUpperCase();
        const name = getCountryName(c, lang);
        const flagSrc = getFlag(code);

        const flagNode = flagSrc ? (
          <img
            src={flagSrc}
            alt={code}
            style={FLAG_IMG_STYLE}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <span>{code}</span>
        );

        const labelDropdown = (
          <span style={DROPDOWN_ITEM_STYLE}>
            {flagNode}
            <span>{name}</span>
          </span>
        );

        const labelSelected = (
          <span style={SELECTED_ITEM_STYLE}>
            {flagNode}
            <span style={NAME_NOWRAP_STYLE}>{name}</span>
          </span>
        );

        const searchText = normalizeSearch(
          `${name} ${c?.names?.pt} ${c?.names?.en} ${c?.names?.es} ${c?.iso3} ${c?.countryCode}`
        );

        return {
          value: code,
          label: labelDropdown,
          'data-label-short': labelSelected,
          'data-search-text': searchText,
          'data-sort-key': name,
        };
      })
      .sort((a, b) =>
        a['data-sort-key'].localeCompare(b['data-sort-key'], undefined, { sensitivity: 'base' })
      );

    return prepared;
  }, [allowedCodes, lang, storedLanguage]);

  // 3. HANDLERS
  const handleChange = useCallback(
    value => {
      const v = (value || '').toUpperCase();
      setSelectedValue(v);
      if (Array.isArray(fullFieldName)) {
        const fieldValue = fullFieldName.reduceRight((acc, key) => ({ [key]: acc }), v);
        form?.setFieldsValue?.(fieldValue);
      } else {
        form?.setFieldsValue?.({ [fullFieldName]: v });
      }
      if (typeof onCountryChange === 'function') onCountryChange(v);
      if (typeof selectProps.onChange === 'function') selectProps.onChange(v);
      setDropdownOpen(false);
    },
    [onCountryChange, selectProps, fullFieldName, form]
  );

  const handleDropdownVisibleChange = useCallback(visible => {
    setDropdownOpen(visible);
  }, []);

  // 4. EFFECTS
  useEffect(() => {
    const current = form?.getFieldValue?.(fullFieldName);
    let shouldUpdate = false;

    if (associatedValueField) {
      const associatedFieldPath = subdocumentField
        ? Array.isArray(subdocumentField)
          ? [...subdocumentField, associatedValueField]
          : [subdocumentField, associatedValueField]
        : associatedValueField;

      const associatedValue = form?.getFieldValue?.(associatedFieldPath);
      const associatedIsEmpty = !associatedValue || String(associatedValue).trim() === '';
      shouldUpdate =
        associatedIsEmpty && (current === undefined || current === null || current === '');
    } else {
      shouldUpdate =
        current === undefined ||
        current === null ||
        current === '' ||
        current !== effectiveCountryCode;
    }

    if (shouldUpdate) {
      setSelectedValue(effectiveCountryCode);
      if (Array.isArray(fullFieldName)) {
        const fieldValue = fullFieldName.reduceRight(
          (acc, key) => ({ [key]: acc }),
          effectiveCountryCode
        );
        form?.setFieldsValue?.(fieldValue);
      } else {
        form?.setFieldsValue?.({ [fullFieldName]: effectiveCountryCode });
      }
    } else if (current && selectedValue !== current) {
      setSelectedValue(current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCountryCode, form, fullFieldName, associatedValueField, subdocumentField]);

  // 9. UI
  return (
    <SimpleFormItem
      name={fullFieldName}
      elementProps={elementProps}
      style={styleItem}
      {..._unsafeItemProps}
    >
      <div
        ref={wrapperRef}
        onMouseDown={e => {
          e.stopPropagation();
        }}
      >
        <Select
          ref={node => {
            selectRef.current = node;
            if (elementRef) {
              if (typeof elementRef === 'function') {
                elementRef(node);
              } else {
                elementRef.current = node;
              }
            }
          }}
          id={flattenFieldName}
          className="select-with-icon"
          size={size}
          value={selectedValue}
          allowClear={allowClear}
          placeholder={placeholder || translateX('select_a_country')}
          style={{ width: '100%', height: 40, ...style }}
          disabled={isDisabled}
          options={options}
          showSearch
          open={dropdownOpen}
          onOpenChange={handleDropdownVisibleChange}
          labelRender={
            optionLabelProp
              ? labelProps => labelProps['data-label-short'] || labelProps.label
              : undefined
          }
          popupMatchSelectWidth={popupMatchSelectWidth}
          filterOption={(input, option) => {
            const q = normalizeSearch(input);
            const txt = normalizeSearch(option?.['data-search-text'] || '');
            return txt.includes(q);
          }}
          onChange={handleChange}
          getPopupContainer={() => wrapperRef.current || document.body}
          virtual={false}
          {...selectProps}
        />
      </div>
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleCountry;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function normalizeSearch(s = '') {
  return s
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
}

function normalizeLang(s = '') {
  const v = (s || '').toUpperCase();
  if (v.startsWith('PT')) return 'pt';
  if (v.startsWith('EN')) return 'en';
  if (v.startsWith('ES')) return 'es';
  return 'en';
}

function getCountryName(country, lang = 'pt') {
  if (!country || typeof country !== 'object') return '';
  return translateX(country.country || 'n_a');
}
